import { FC, useCallback } from 'react';

import { SwimlaneItem } from '@rikstv/play-common/src/forces/assetDetails/assetDetails.types';
import { RequiredProps } from '@rikstv/play-common/src/utils/types/typeUtils';

import { FadeIn } from '../../../components/animation/fade-in/FadeIn';
import { useSwimlane } from '../../../components/swimlane/useSwimlane';
import { Swimlane } from '../../pages/types';
import { ContinueWatchingItem } from '../styles/ContinueWatchingItem';
import { FeaturedItem } from '../styles/FeaturedItem';
import { LiveChannelItem } from '../styles/LiveChannelItem';
import { MenuSwimlaneItem } from '../styles/MenuSwimlaneItem';
import { PortraitItem } from '../styles/PortraitItem';
import { ContextMenuItem, StandardItem } from '../styles/StandardItem';
import { SwimlaneScroller } from '../swimlaneScroller/SwimlaneScroller';
import { getSwimlaneItemTypes } from '../utils/getSwimlaneItemTypes';

export interface BasicCoreScrollSwimlaneProps {
  swimlane: Swimlane;
  onItemClicked?: (item: SwimlaneItem, index: number) => void;
  contextMenuItems?: ContextMenuItem[];
}

export const BasicCoreScrollSwimlane: FC<BasicCoreScrollSwimlaneProps> = ({
  swimlane,
  onItemClicked,
  contextMenuItems,
}) => {
  const { swimlaneItems, error, filterSwimlaneItemById, isPlaceholder } = useSwimlane(swimlane);

  const onClick = useCallback(
    (item: SwimlaneItem, elementIndex: number) => {
      onItemClicked?.(item, elementIndex);
    },
    [onItemClicked]
  );

  // Return null on error or when returned list has zero items
  if (error || swimlaneItems?.length === 0) {
    return null;
  }

  const swimlaneItemType = getSwimlaneItemTypes(swimlaneItems, swimlane.style, swimlane.type);

  return (
    <FadeIn>
      <SwimlaneScroller swimlane={swimlane} isPlaceHolder={isPlaceholder}>
        {/* menu items */}
        {swimlaneItemType === 'menuItems' &&
          swimlaneItems.map((item, index) => (
            <MenuSwimlaneItem
              key={swimlane.id + item.id}
              asset={item}
              swimlaneType={swimlane.type}
              swimlaneStyle={swimlane.style}
              onClick={() => onClick(item, index)}
              noMargin
            />
          ))}
        {/* standard items */}
        {swimlaneItemType === 'standardItems' &&
          swimlaneItems.map((item, index) => (
            <StandardItem
              key={'standard-item-' + item.cardId}
              asset={item}
              swimlaneType={swimlane.type}
              swimlaneStyle={swimlane.style}
              onClick={() => onClick(item, index)}
              noMargin
              contextMenuItems={contextMenuItems}
            />
          ))}

        {/* featured items */}
        {swimlaneItemType === 'featuredItems' &&
          swimlaneItems.map((item, index) => (
            <FeaturedItem
              key={'featured-item-' + item.cardId}
              asset={item}
              swimlaneType={swimlane.type}
              swimlaneStyle={swimlane.style}
              onClick={() => onClick(item, index)}
              noMargin
            />
          ))}

        {/* portraits */}
        {swimlaneItemType === 'portraitItems' &&
          swimlaneItems.map((item, index) => (
            <PortraitItem
              key={'portrait-item-' + item.cardId}
              asset={item}
              swimlaneType={swimlane.type}
              swimlaneStyle={swimlane.style}
              onClick={() => onClick(item, index)}
              noMargin
              hideCaption={swimlane.style === 'HeroPortrait'}
            />
          ))}

        {/* live channel items */}
        {swimlaneItemType === 'liveChannels' &&
          (swimlaneItems as RequiredProps<SwimlaneItem, 'channel'>[]).map((item, index) => (
            <LiveChannelItem asset={item} key={'channel-item-' + item.cardId} onClick={() => onClick(item, index)} />
          ))}

        {/* continue watching items */}
        {swimlaneItemType === 'continueWatchingItems' &&
          swimlaneItems.map((item, index) => (
            <ContinueWatchingItem
              swimlane={swimlane}
              key={'continue-item-' + item.cardId}
              asset={item}
              preventNavigation={false}
              setOptimisticFilter={filterSwimlaneItemById}
              onClick={() => onClick(item, index)}
            />
          ))}

        {/* MyList items */}
        {swimlaneItemType === 'myListItems' &&
          swimlaneItems.map((item, index) => (
            <StandardItem
              key={'standard-item-' + item.cardId}
              asset={item}
              swimlaneType={swimlane.type}
              swimlaneStyle={swimlane.style}
              onClick={() => onClick(item, index)}
              noMargin
            />
          ))}
      </SwimlaneScroller>
    </FadeIn>
  );
};
